import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, Shield, Lock, Database, Mail } from 'lucide-react';

const PrivacyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center space-x-2 mb-6">
            <Eye className="w-10 h-10 text-purple-400" />
            <span className="text-2xl font-bold gradient-text serif-display">Coercive Control Patterns</span>
          </div>
          
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center space-x-2 text-purple-400 hover:text-purple-300 transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>
        </div>

        {/* Main Content */}
        <div className="glass rounded-2xl p-8">
          <div className="flex items-center space-x-3 mb-8">
            <Shield className="w-8 h-8 text-purple-400" />
            <h1 className="text-4xl font-bold text-white serif-display">Privacy Policy</h1>
          </div>

          <div className="text-gray-300 space-y-6 leading-relaxed">
            <p className="text-lg text-purple-200">
              <strong>Last Updated:</strong> January 1, 2025
            </p>

            {/* Introduction */}
            <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 rounded-lg p-6 border border-purple-500/30">
              <p className="text-lg leading-relaxed">
                We value your trust and privacy. CoerciveControlPatterns.com ("we," "our," or "the Service") analyzes uploaded communication data to help customers identify potential patterns of coercive control and psychological manipulation.
              </p>
            </div>

            {/* Key Points */}
            <div className="bg-slate-800/50 rounded-lg p-6">
              <h2 className="text-2xl font-bold text-white mb-4">Key Privacy Points</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-300">
                <li><strong>We only use your uploaded data to generate your report.</strong></li>
                <li><strong>All uploaded files and generated reports are automatically deleted within 24 hours</strong> (unless you download them first).</li>
                <li><strong>We never sell, share, or review your data.</strong></li>
                <li>Our reports are for informational purposes only and are not legal, therapeutic, or clinical advice.</li>
                <li>You are responsible for how you use the information provided.</li>
                <li>We operate under the laws of Wyoming, USA, and reserve the right to restrict access from certain jurisdictions.</li>
              </ul>
            </div>

            {/* Section 1 */}
            <div className="bg-slate-800/50 rounded-lg p-6">
              <h2 className="text-2xl font-bold text-white mb-4">1. Definitions</h2>
              
              <div className="space-y-3">
                <p><strong>"Service"</strong> refers to the website CoerciveControlPatterns.com and all related tools, software, and features.</p>
                <p><strong>"Customer"</strong> means any person or entity who accesses, uses, or pays for the Service.</p>
                <p><strong>"Data"</strong> means any communications, documents, or materials you upload to the Service.</p>
                <p><strong>"Report"</strong> means the automated analysis output generated from your Data by our AI tools.</p>
              </div>
            </div>

            {/* Section 2 */}
            <div className="bg-slate-800/50 rounded-lg p-6">
              <div className="flex items-center space-x-2 mb-4">
                <Database className="w-6 h-6 text-blue-400" />
                <h2 className="text-2xl font-bold text-white">2. Information We Collect</h2>
              </div>
              
              <p className="mb-4">We collect and process only the information necessary to provide the Service:</p>
              
              <ul className="list-disc list-inside space-y-2 text-gray-300">
                <li><strong>Uploaded Documents:</strong> Files, messages, or communication logs you provide for analysis.</li>
                <li><strong>Account & Payment Data:</strong> Name, email address, and billing information to process your order and deliver your report.</li>
                <li><strong>Technical Data:</strong> Basic browser, device, and usage data (e.g., IP address, timestamps) to operate and secure the Service.</li>
              </ul>
            </div>

            {/* Section 3 */}
            <div className="bg-slate-800/50 rounded-lg p-6">
              <div className="flex items-center space-x-2 mb-4">
                <Lock className="w-6 h-6 text-green-400" />
                <h2 className="text-2xl font-bold text-white">3. How We Use Your Information</h2>
              </div>
              
              <p className="mb-4">We use your information solely to:</p>
              
              <ul className="list-disc list-inside space-y-2 text-gray-300">
                <li>Generate the requested communication pattern analysis report.</li>
                <li>Process payments and deliver receipts.</li>
                <li>Maintain and improve the Service's functionality, security, and performance.</li>
              </ul>
              
              <div className="mt-6 p-4 bg-red-900/20 border border-red-600/30 rounded-lg">
                <p className="font-semibold text-red-200 mb-2">We do not:</p>
                <ul className="list-disc list-inside space-y-1 text-red-200">
                  <li>Access, view, or manually review your uploaded content.</li>
                  <li>Sell, rent, or share your data with third parties.</li>
                  <li>Use your data for marketing without your explicit consent.</li>
                </ul>
              </div>
            </div>

            {/* Section 4 */}
            <div className="bg-slate-800/50 rounded-lg p-6">
              <div className="flex items-center space-x-2 mb-4">
                <Shield className="w-6 h-6 text-purple-400" />
                <h2 className="text-2xl font-bold text-white">4. Data Deletion and Retention</h2>
              </div>
              
              <p className="mb-4">We are committed to secure, minimal data handling.</p>
              
              <ul className="list-disc list-inside space-y-2 text-gray-300">
                <li><strong>All uploaded files and generated reports are automatically and permanently deleted from our servers within 24 hours of upload</strong> (or immediately after download, if earlier).</li>
                <li>If you do not download a report, it will still be deleted within 24 hours.</li>
                <li>We do not store, retain, or archive your data beyond this period.</li>
              </ul>
              
              <p className="mt-4 text-blue-200">
                The only data retained is limited, non-identifiable metadata (e.g., number of pages processed) used for internal analytics and service optimization.
              </p>
            </div>

            {/* Section 5 */}
            <div className="bg-slate-800/50 rounded-lg p-6">
              <h2 className="text-2xl font-bold text-white mb-4">5. AI and Automated Analysis Disclaimer</h2>
              
              <p className="mb-4">Our reports are generated by AI-powered pattern detection tools and are intended for informational and educational purposes only.</p>
              
              <ul className="list-disc list-inside space-y-2 text-gray-300">
                <li>We do not verify, authenticate, or guarantee the accuracy, completeness, or authenticity of uploaded data or generated reports.</li>
                <li>Reports do not constitute legal, psychological, or clinical advice and should not be relied upon as such.</li>
                <li>Customers should consult qualified attorneys, therapists, or advocates before making legal or mental health decisions.</li>
              </ul>
            </div>

            {/* Section 6 */}
            <div className="bg-slate-800/50 rounded-lg p-6">
              <h2 className="text-2xl font-bold text-white mb-4">6. Non-Therapy / Non-Diagnostic Statement</h2>
              
              <p className="mb-4">The Service is not a substitute for therapy, counseling, diagnosis, or medical treatment. We do not provide mental health services or diagnostic evaluations.</p>
              
              <p className="text-amber-200">
                If you are experiencing abuse, coercive control, or emotional distress, we encourage you to seek assistance from a licensed professional or support organization.
              </p>
            </div>

            {/* Section 7 */}
            <div className="bg-slate-800/50 rounded-lg p-6">
              <h2 className="text-2xl font-bold text-white mb-4">7. International Use and Data Transfers</h2>
              
              <p className="mb-4">We offer global access to the Service, but:</p>
              
              <ul className="list-disc list-inside space-y-2 text-gray-300">
                <li>We may restrict or deny access from certain jurisdictions at our sole discretion.</li>
                <li>By using the Service, you consent to the processing of your data in the United States.</li>
                <li>If you are located in the EU, UK, or other regions with data protection laws, we will take reasonable steps to comply with applicable requirements.</li>
              </ul>
            </div>

            {/* Section 8 */}
            <div className="bg-slate-800/50 rounded-lg p-6">
              <h2 className="text-2xl font-bold text-white mb-4">8. Your Rights</h2>
              
              <p className="mb-4">Depending on your jurisdiction, you may have the right to:</p>
              
              <ul className="list-disc list-inside space-y-2 text-gray-300">
                <li>Access and receive a copy of your personal data.</li>
                <li>Request deletion of your personal data.</li>
                <li>Withdraw consent to processing.</li>
              </ul>
              
              <p className="text-blue-200 mt-4">
                To exercise these rights, contact us at <strong>support@coercivecontrolpatterns.com</strong>
              </p>
            </div>

            {/* Section 9 */}
            <div className="bg-slate-800/50 rounded-lg p-6">
              <h2 className="text-2xl font-bold text-white mb-4">9. Security Measures</h2>
              
              <p className="text-gray-300">
                We use industry-standard security protocols to protect your data, including encryption in transit and at rest, access controls, and server hardening. However, no online system is 100% secure, and we cannot guarantee absolute security.
              </p>
            </div>

            {/* Section 10 */}
            <div className="bg-slate-800/50 rounded-lg p-6">
              <h2 className="text-2xl font-bold text-white mb-4">10. Children's Privacy</h2>
              
              <p className="text-gray-300">
                The Service is not intended for individuals under 18. We do not knowingly collect personal data from children. If you believe a minor has provided us with data, please contact us immediately.
              </p>
            </div>

            {/* Section 11 */}
            <div className="bg-slate-800/50 rounded-lg p-6">
              <h2 className="text-2xl font-bold text-white mb-4">11. Changes to This Policy</h2>
              
              <p className="text-gray-300">
                We may update this Privacy Policy periodically. Any changes will be posted on this page with a new "Last Updated" date. Continued use of the Service constitutes acceptance of the revised policy.
              </p>
            </div>

            {/* Contact Information */}
            <div className="bg-gradient-to-r from-purple-900/50 to-blue-900/50 rounded-lg p-6 border border-purple-500/30">
              <div className="flex items-center space-x-2 mb-4">
                <Mail className="w-6 h-6 text-purple-400" />
                <h2 className="text-2xl font-bold text-white">12. Contact Us</h2>
              </div>
              
              <p className="text-gray-300 mb-4">If you have questions about this Privacy Policy or your data, you can contact us at:</p>
              
              <div className="space-y-2 text-gray-300">
                <p><strong>Email:</strong> support@coercivecontrolpatterns.com</p>
                <p><strong>Jurisdiction:</strong> State of Wyoming, United States</p>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;